import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Card, CircularProgress, Alert, Snackbar, Grid, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { getSalesLead, updateSalesLead } from 'services/salesLeadService';
import { getLeadSignups } from 'services/pilotApprovalService';
import { SALES_LEAD_FIELDS, LEAD_TYPE_OPTIONS, requiredFor, landingUrlFor } from '../fields';

const validationSchema = Yup.object({
  leadType: Yup.string().oneOf(['dealer', 'rep_card']).required(),
  contactName: Yup.string().when('leadType', { is: 'dealer', then: Yup.string().required('Contact name is required') }),
  businessName: Yup.string().when('leadType', { is: 'dealer', then: Yup.string().required('Business name is required') }),
  salesRep: Yup.string().when('leadType', { is: 'rep_card', then: Yup.string().required('Sales rep is required') }),
  salesRepPhone: Yup.string().when('leadType', { is: 'rep_card', then: Yup.string().required('Sales rep phone is required') }),
  businessEmail: Yup.string().email('A valid email must be provided'),
});

const blank = { leadType: 'dealer', ...SALES_LEAD_FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {}) };

function SalesLeadEdit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');
  const [lead, setLead] = useState(null);
  const [signups, setSignups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const data = await getSalesLead(leadId);
        setLead(data);
      } catch (err) {
        console.error('Error loading lead:', err);
        setSnackbar({ open: true, message: 'Failed to load lead', severity: 'error' });
      }
      try {
        const rows = await getLeadSignups(leadId);
        setSignups(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error('Error loading lead signups:', err);
      }
    })();
  }, [leadId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: lead
      ? { ...blank, leadType: lead.leadType || 'dealer', ...SALES_LEAD_FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: lead[f.name] ?? '' }), {}) }
      : blank,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        const payload = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '' && v !== null));
        await updateSalesLead(leadId, payload);
        setSnackbar({ open: true, message: 'Sales lead updated!', severity: 'success' });
        setTimeout(() => navigate('/sales-leads'), 1200);
      } catch (err) {
        console.error('Error updating lead:', err);
        setSnackbar({ open: true, message: 'An unexpected error occurred!', severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const url = lead?.queryString ? landingUrlFor(lead.queryString) : '';

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h6" mb={1}>Edit Sales Lead</SoftTypography>
            {lead?.queryString && (
              <SoftBox mb={3}>
                <SoftTypography variant="caption" fontWeight="bold">Personalized link ({lead.status || 'new'})</SoftTypography>
                <SoftBox display="flex" gap={1} alignItems="center">
                  <SoftInput value={url} readOnly />
                  <SoftButton variant="outlined" color="info" onClick={() => navigator.clipboard?.writeText(url)}>
                    Copy
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            )}
            <SoftBox mb={3}>
              <SoftTypography variant="caption" fontWeight="bold">
                Signups from this link — Initiated: {lead?.signupCount || 0}
                {lead?.leadType === 'rep_card'
                  ? ` · Converted: ${lead?.conversionCount || 0}`
                  : ` · Status: ${lead?.status || 'new'}`}
              </SoftTypography>
              {signups.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ display: 'table-header-group' }}>
                      <TableRow>
                        <TableCell>Dealership</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {signups.map((s) => (
                        <TableRow key={s.pk}>
                          <TableCell>{s.dealershipName || '—'}</TableCell>
                          <TableCell>{s.email}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={s.status}
                              color={s.status === 'activated' ? 'success' : s.status === 'rejected' ? 'error' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SoftBox>
            <form onSubmit={formik.handleSubmit}>
              <SoftBox mb={2}>
                <SoftTypography variant="caption" fontWeight="bold">Lead Type *</SoftTypography>
                <select
                  name="leadType"
                  value={formik.values.leadType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d2d6da', fontSize: '14px' }}
                >
                  {LEAD_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </SoftBox>
              <Grid container spacing={2}>
                {SALES_LEAD_FIELDS.map((f) => (
                  <Grid item xs={12} sm={6} key={f.name}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      {f.label}{requiredFor(f.name, formik.values.leadType) ? ' *' : ''}
                    </SoftTypography>
                    <SoftInput
                      placeholder={f.label}
                      name={f.name}
                      type={f.type || 'text'}
                      value={formik.values[f.name]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched[f.name] && Boolean(formik.errors[f.name])}
                    />
                    {formik.touched[f.name] && formik.errors[f.name] && (
                      <SoftTypography variant="caption" color="error">{formik.errors[f.name]}</SoftTypography>
                    )}
                  </Grid>
                ))}
              </Grid>
              <SoftBox display="flex" gap={2} mt={3}>
                {!submitting ? (
                  <>
                    <SoftButton type="submit" variant="gradient" color="info" disabled={!formik.isValid}>
                      Save
                    </SoftButton>
                    <SoftButton variant="gradient" color="secondary" onClick={() => navigate('/sales-leads')}>
                      Cancel
                    </SoftButton>
                  </>
                ) : (
                  <CircularProgress size={24} />
                )}
              </SoftBox>
            </form>
          </SoftBox>
        </Card>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default SalesLeadEdit;
